import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "./client";

// ===== Health =====
export function useHealth() {
  return useQuery({
    queryKey: ["health"],
    queryFn: async () => {
      const r = await apiClient.get("/healthz");
      return r.data;
    },
    refetchInterval: 30_000,
  });
}

export function useReadiness() {
  return useQuery({
    queryKey: ["readiness"],
    queryFn: async () => {
      const r = await apiClient.get("/readyz");
      return r.data;
    },
    refetchInterval: 10_000,
  });
}

export function useServices() {
  return useQuery({
    queryKey: ["services"],
    queryFn: async () => {
      const r = await apiClient.get("/services");
      return r.data;
    },
    refetchInterval: 5_000,
  });
}

// ===== Auth =====
export function useMe(enabled = true) {
  return useQuery({
    queryKey: ["me"],
    enabled,
    queryFn: async () => {
      const r = await apiClient.get("/auth/me");
      return r.data;
    },
  });
}

export function useLogin() {
  return useMutation({
    mutationFn: async (creds: { email: string; password: string }) => {
      const r = await apiClient.post("/auth/login/json", creds);
      return r.data;
    },
    onSuccess: (data) => {
      localStorage.setItem("wow_access_token", data.access_token);
      localStorage.setItem("wow_refresh_token", data.refresh_token);
    },
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await apiClient.post("/auth/logout");
    },
    onSettled: () => {
      localStorage.removeItem("wow_access_token");
      localStorage.removeItem("wow_refresh_token");
      qc.clear();
      window.location.href = "/login";
    },
  });
}

// ===== Dashboard =====
export function useDashboard() {
  return useQuery({
    queryKey: ["dashboard"],
    queryFn: async () => {
      const r = await apiClient.get("/dashboard/");
      return r.data;
    },
    refetchInterval: 15_000,
  });
}

// ===== Investigations =====
export function useInvestigations(params?: { status?: string; page?: number; size?: number }) {
  return useQuery({
    queryKey: ["investigations", params],
    queryFn: async () => {
      const r = await apiClient.get("/investigations/", { params });
      return r.data;
    },
    refetchInterval: 30_000,
  });
}

export function useInvestigation(id: string | null | undefined) {
  return useQuery({
    queryKey: ["investigation", id],
    enabled: !!id,
    queryFn: async () => {
      const r = await apiClient.get(`/investigations/${id}`);
      return r.data;
    },
  });
}

export function useCreateInvestigation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      title: string;
      target: string;
      target_type?: string;
      description?: string;
      priority?: string;
      tools_planned?: string[];
      tags?: string[];
    }) => {
      const r = await apiClient.post("/investigations/", data);
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["investigations"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
}

export function useDeleteInvestigation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/investigations/${id}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["investigations"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
}

// ===== Reports =====
export function useReports(params?: { page?: number; size?: number; report_type?: string; investigation_id?: string }) {
  return useQuery({
    queryKey: ["reports", params],
    queryFn: async () => {
      const r = await apiClient.get("/reports/", { params });
      return r.data;
    },
    refetchInterval: 30_000,
  });
}

// ===== Audit =====
export function useAudit(params?: { page?: number; size?: number; action?: string }) {
  return useQuery({
    queryKey: ["audit", params],
    queryFn: async () => {
      const r = await apiClient.get("/audit/", { params });
      return r.data;
    },
    refetchInterval: 30_000,
  });
}

// ===== Chat =====
export interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: string;
  status?: "pending" | "ok" | "error";
  model?: string;
  tokens?: number;
}

export function useChatHistory(investigationId: string | null) {
  return useQuery({
    queryKey: ["chat-history", investigationId],
    enabled: !!investigationId,
    queryFn: async () => {
      // For now chat history is per-session only (memory); this stub returns empty.
      return [] as ChatMessage[];
    },
  });
}

export function useSendChat() {
  return useMutation({
    mutationFn: async (vars: {
      message: string;
      investigation_id?: string;
      history?: { role: "user" | "assistant"; content: string }[];
      model?: string;
    }) => {
      const r = await apiClient.post("/chat/", vars);
      return r.data;
    },
  });
}

// ===== Service actions =====
export function useServiceAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { name: string; action: "start" | "stop" | "restart" }) => {
      const r = await apiClient.post("/services/action", vars);
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["services"] });
    },
  });
}

// ===== LLM =====
export function useLlmModels() {
  return useQuery({
    queryKey: ["llm-models"],
    queryFn: async () => {
      const r = await apiClient.get("/llm/models");
      return r.data;
    },
  });
}
